import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import api from './api';

const KATEGORI_ETIKETLER = {
  yukseltme_talebi: { label: 'Hesap Yükseltme', renk: 'warning' },
  teknik_sorun:     { label: 'Teknik Sorun',    renk: 'danger'  },
  fatura:           { label: 'Fatura',           renk: 'info'    },
  diger:            { label: 'Diğer',            renk: 'secondary' },
};

const DURUM_ETIKETLER = {
  acik:       { label: 'Açık',       renk: 'success' },
  beklemede:  { label: 'Beklemede',  renk: 'warning' },
  cozuldu:    { label: 'Çözüldü',    renk: 'primary' },
  reddedildi: { label: 'Reddedildi', renk: 'danger'  },
};

function AdminTicketDetay() {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket]           = useState(null);
  const [mesajlar, setMesajlar]       = useState([]);
  const [yukleniyor, setYukleniyor]   = useState(true);
  const [hata, setHata]               = useState(null);
  const [yeniMesaj, setYeniMesaj]     = useState('');
  const [gonderiyor, setGonderiyor]   = useState(false);
  const [gonderHata, setGonderHata]   = useState(null);

  const [durumModal, setDurumModal]         = useState(false);
  const [seciliDurum, setSeciliDurum]       = useState('');
  const [sureAy, setSureAy]                 = useState(1);
  const [durumYukleniyor, setDurumYukleniyor] = useState(false);
  const [durumHata, setDurumHata]           = useState(null);

  const mesajlarSonuRef = useRef(null);
  const sseAktifRef     = useRef(false);

  const scrollToBottom = () =>
    mesajlarSonuRef.current?.scrollIntoView({ behavior: 'smooth' });

  /* ── Ticket yükle ─────────────────────────────────────────── */
  const fetchTicket = useCallback(async () => {
    try {
      const res = await api.get(`/admin/destek/ticket/${ticketId}`);
      setTicket(res.data);
      setMesajlar(res.data.mesajlar || []);
    } catch (e) {
      setHata(e.response?.status === 404
        ? 'Ticket bulunamadı.'
        : 'Ticket yüklenirken hata oluştu.');
    } finally {
      setYukleniyor(false);
    }
  }, [ticketId]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);
  useEffect(() => { scrollToBottom(); }, [mesajlar]);

  /* ── SSE ──────────────────────────────────────────────────── */
  useEffect(() => {
    if (yukleniyor || hata) return;
    sseAktifRef.current = true;
    const token = window._accessToken || localStorage.getItem('accessToken');
    if (!token) return;

    const url = `${process.env.REACT_APP_API_URL}/admin/destek/stream`;

    const connect = async () => {
      try {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!response.ok) return;

        const reader  = response.body.getReader();
        const decoder = new TextDecoder();

        while (sseAktifRef.current) {
          const { value, done } = await reader.read();
          if (done) break;
          const lines = decoder.decode(value, { stream: true }).split('\n');
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const mesaj = JSON.parse(line.slice(6));
              if (mesaj.ticket_id === parseInt(ticketId)) {
                setMesajlar(prev =>
                  prev.find(m => m.id === mesaj.id) ? prev : [...prev, mesaj]
                );
              }
            } catch { /* JSON parse hatası */ }
          }
        }
      } catch {
        if (sseAktifRef.current) setTimeout(connect, 3000);
      }
    };

    connect();
    return () => { sseAktifRef.current = false; };
  }, [yukleniyor, hata, ticketId]);

  /* ── Mesaj gönder ─────────────────────────────────────────── */
  const handleMesajGonder = async (e) => {
    e.preventDefault();
    const icerik = yeniMesaj.trim();
    if (!icerik) return;
    setGonderiyor(true);
    setGonderHata(null);
    try {
      const res = await api.post(
        `/admin/destek/ticket/${ticketId}/mesaj`,
        { icerik }
      );
      setMesajlar(prev =>
        prev.find(m => m.id === res.data.id) ? prev : [...prev, res.data]
      );
      setYeniMesaj('');
      setTicket(prev => prev ? { ...prev, durum: 'beklemede' } : prev);
    } catch (err) {
      setGonderHata(err.response?.data?.mesaj || 'Mesaj gönderilemedi.');
    } finally {
      setGonderiyor(false);
    }
  };

  /* ── Durum güncelle ───────────────────────────────────────── */
  const handleDurumGuncelle = async () => {
    if (!seciliDurum) return;
    setDurumYukleniyor(true);
    setDurumHata(null);
    try {
      const body = { durum: seciliDurum };
      if (seciliDurum === 'cozuldu' && ticket?.kategori === 'yukseltme_talebi') {
        body.sure_ay = Number(sureAy) || 1;
      }
      const res = await api.patch(
        `/admin/destek/ticket/${ticketId}/durum`,
        body
      );
      setTicket(res.data.ticket);
      setDurumModal(false);
    } catch (err) {
      console.error('PATCH /durum hata:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
        code: err.code,
      });
      const msg = err.response?.data?.mesaj;
      const status = err.response?.status;
      if (!status) {
        setDurumHata('Bağlantı hatası — backend çalışıyor mu? (F12 → Console / Network)');
      } else {
        setDurumHata(msg || `Hata ${status}: Durum güncellenemedi.`);
      }
    } finally {
      setDurumYukleniyor(false);
    }
  };

  /* ── Render ───────────────────────────────────────────────── */
  if (yukleniyor) return (
    <Container fluid className="py-5 text-center">
      <Spinner animation="border" variant="primary" />
    </Container>
  );

  if (hata) return (
    <Container fluid className="py-4">
      <Alert variant="danger">{hata}</Alert>
      <Button variant="outline-secondary" onClick={() => navigate('/admin/destek')}>
        <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: '1rem' }}>arrow_back</span>
        Geri
      </Button>
    </Container>
  );

  const kat    = KATEGORI_ETIKETLER[ticket.kategori] ?? KATEGORI_ETIKETLER.diger;
  const dur    = DURUM_ETIKETLER[ticket.durum]       ?? DURUM_ETIKETLER.acik;
  const kapali = ['cozuldu', 'reddedildi'].includes(ticket.durum);

  return (
    <Container fluid className="py-4 px-3 px-md-4" style={{ maxWidth: 860 }}>

      {/* ── Üst bar ── */}
      <div className="d-flex align-items-start gap-2 mb-4 flex-wrap">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/admin/destek')} className="mt-1">
          <span className="material-symbols-outlined align-middle" style={{ fontSize: '1.1rem' }}>arrow_back</span>
        </Button>
        <div className="flex-grow-1">
          <h5 className="fw-bold mb-1">{ticket.baslik}</h5>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <Badge bg={kat.renk} className="text-dark">{kat.label}</Badge>
            <Badge bg={dur.renk}>{dur.label}</Badge>
            <span className="text-muted small">#{ticket.id}</span>
            <span className="text-muted small">
              <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: '0.9rem' }}>person</span>
              {ticket.kullanici_adi}
            </span>
          </div>
        </div>
        {!kapali && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => { setSeciliDurum(''); setDurumHata(null); setDurumModal(true); }}
          >
            <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: '1rem' }}>tune</span>
            Durumu Güncelle
          </Button>
        )}
      </div>

      {/* ── Mesaj listesi ── */}
      <div
        className="border rounded-3 p-3 mb-3"
        style={{
          maxHeight: 480,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          backgroundColor: 'var(--bg-secondary)',
        }}
      >
        {mesajlar.length === 0
          ? <p className="text-muted text-center my-4">Henüz mesaj yok.</p>
          : mesajlar.map(mesaj => {
              const adminMesaji = mesaj.gonderen_rol === 'admin';
              return (
                <div key={mesaj.id} className={`d-flex ${adminMesaji ? 'justify-content-end' : 'justify-content-start'}`}>
                  <div
                    className={`px-3 py-2 rounded-3 ${adminMesaji ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                    style={{ maxWidth: '75%', wordBreak: 'break-word' }}
                  >
                    <div className={`fw-bold small mb-1 ${adminMesaji ? 'text-white-50' : 'opacity-75'}`}>
                      <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: '0.9rem' }}>
                        {adminMesaji ? 'support_agent' : 'person'}
                      </span>
                      {adminMesaji ? 'Siz (Admin)' : mesaj.kullanici_adi}
                    </div>
                    <div>{mesaj.icerik}</div>
                    <div className={`small mt-1 ${adminMesaji ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '0.7rem' }}>
                      {new Date(mesaj.tarih).toLocaleString('tr-TR', {
                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              );
            })
        }
        <div ref={mesajlarSonuRef} />
      </div>

      {/* ── Kapalı bildirim / Mesaj formu ── */}
      {kapali ? (
        <Alert variant={ticket.durum === 'cozuldu' ? 'success' : 'secondary'} className="text-center mb-0">
          <span className="material-symbols-outlined align-middle me-2">
            {ticket.durum === 'cozuldu' ? 'check_circle' : 'cancel'}
          </span>
          Bu ticket {ticket.durum === 'cozuldu' ? 'çözüldü' : 'reddedildi'}.
        </Alert>
      ) : (
        <Form onSubmit={handleMesajGonder}>
          {gonderHata && <Alert variant="danger" className="py-2 small mb-2">{gonderHata}</Alert>}
          <div className="d-flex gap-2">
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Kullanıcıya yanıt yazın..."
              value={yeniMesaj}
              onChange={e => setYeniMesaj(e.target.value)}
              disabled={gonderiyor}
              onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleMesajGonder(e); }}
            />
            <Button
              type="submit"
              variant="primary"
              disabled={gonderiyor || !yeniMesaj.trim()}
              style={{ alignSelf: 'flex-end', minWidth: 46 }}
            >
              {gonderiyor
                ? <Spinner size="sm" animation="border" />
                : <span className="material-symbols-outlined" style={{ fontSize: '1.2rem' }}>send</span>
              }
            </Button>
          </div>
          <div className="text-muted small mt-1">Ctrl+Enter ile hızlı gönder</div>
        </Form>
      )}

      {/* ── Durum Güncelle Modal ── */}
      <Modal show={durumModal} onHide={() => !durumYukleniyor && setDurumModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="material-symbols-outlined align-middle me-2">tune</span>
            Durumu Güncelle
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {durumHata && <Alert variant="danger" className="py-2">{durumHata}</Alert>}

          <p className="small text-muted mb-3">
            Ticket: <strong>#{ticket.id} — {ticket.baslik}</strong>
          </p>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Yeni Durum Seç</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {Object.entries(DURUM_ETIKETLER).map(([key, val]) => (
                <Button
                  key={key}
                  size="sm"
                  variant={seciliDurum === key ? val.renk : `outline-${val.renk}`}
                  onClick={() => setSeciliDurum(key)}
                  className={seciliDurum === key ? 'fw-bold' : ''}
                >
                  {seciliDurum === key && (
                    <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: '0.85rem' }}>check</span>
                  )}
                  {val.label}
                </Button>
              ))}
            </div>
          </Form.Group>

          {/* Yükseltme onayı → abonelik süresi */}
          {seciliDurum === 'cozuldu' && ticket.kategori === 'yukseltme_talebi' && (
            <Form.Group className="mt-3">
              <Form.Label className="fw-semibold">Abonelik Süresi (Ay)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={24}
                value={sureAy}
                onChange={e => setSureAy(e.target.value)}
              />
              <Form.Text className="text-muted">
                Kullanıcının demo hesabı bu süreyle aktif normal hesaba dönüşecek.
              </Form.Text>
            </Form.Group>
          )}

          {seciliDurum === 'reddedildi' && (
            <Alert variant="warning" className="mt-3 py-2 small mb-0">
              Reddedilirse kullanıcı yeni talep oluşturabilir.
            </Alert>
          )}
          {seciliDurum === 'cozuldu' && ticket.kategori !== 'yukseltme_talebi' && (
            <Alert variant="info" className="mt-3 py-2 small mb-0">
              Ticket çözüldü olarak kapatılacak, kullanıcı bilgilendirilecek.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDurumModal(false)}
            disabled={durumYukleniyor}
          >
            İptal
          </Button>
          <Button
            variant={seciliDurum ? (DURUM_ETIKETLER[seciliDurum]?.renk || 'primary') : 'primary'}
            onClick={handleDurumGuncelle}
            disabled={!seciliDurum || durumYukleniyor}
          >
            {durumYukleniyor
              ? <><Spinner size="sm" animation="border" className="me-2" />Güncelleniyor...</>
              : 'Güncelle'
            }
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default AdminTicketDetay;
